"use client";

import { cn } from "@ui/lib/utils";
import { Plus } from "lucide-react";
import * as m from "motion/react-m";
import { useCallback, useState } from "react";

import { DottedBorder } from "./GradientBorder";

export default function Accordion({
  children,
  onClick,
  title,
  open: controlledOpen,
  flat = false,
}: {
  children: React.ReactNode;
  flat?: boolean;
  onClick?: () => void;
  open?: boolean;
  title: string;
}) {
  const [internalOpen, setInternalOpen] = useState(!!controlledOpen);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleClick = useCallback(() => {
    if (isControlled) {
      onClick?.();
    } else {
      setInternalOpen(!internalOpen);
    }
  }, [isControlled, onClick, internalOpen]);

  return (
    <div className='relative flex flex-col justify-start'>
      {!flat && <DottedBorder side='bottom' />}
      <div className={cn("group flex flex-col overflow-hidden rounded-lg", !flat && open && "cursor-default")}>
        <button aria-expanded={open} className='w-full text-start' onClick={handleClick} tabIndex={0}>
          <m.div
            animate={{
              height: !flat && open ? 0 : "auto",
              opacity: !flat && open ? 0 : 1,
            }}
            className={cn(
              "flex w-full justify-between overflow-hidden border-overlay-darker transition-colors duration-300 ease-in-out group-active:border-overlay-darker-plus",
            )}
            initial={false}
            transition={{ duration: 0.3, type: "spring", stiffness: 320, damping: 32 }}
          >
            <span
              className={cn("p-16 pl-0 transition-all duration-300", open && !flat ? "f-heading-05" : "f-paragraph-01")}
            >
              <h3 className='origin-left transition-transform duration-150 ease-out'>{title}</h3>
            </span>
            <div className='flex items-center'>
              <m.div
                animate={{ rotate: open ? 45 : 0 }}
                transition={{ duration: 0.3, type: "spring", stiffness: 320, damping: 10 }}
              >
                <Plus className='h-20 w-20' />
              </m.div>
            </div>
          </m.div>
        </button>
        <m.div
          animate={{
            height: open ? "auto" : 0,
            opacity: open ? 1 : 0,
          }}
          aria-hidden={open ? "false" : "true"}
          className={cn("relative overflow-hidden")}
          initial={false}
          transition={{ duration: 0.3, type: "spring", stiffness: 250, damping: 32 }}
        >
          {children}
        </m.div>
      </div>
    </div>
  );
}
